import {
  type AnalyticsEnvironmentConfig,
  getGoogleAnalyticsData,
  type InitializeSegmentOptions,
  initializeSegment,
  insertCookieTrackingScript,
} from '@susu/analytics-client'

import type { Environment } from '../App'
import type {
  ApplicationIdValueType,
  AvoEnv,
  CancellationClickedProperties,
  CancellationConfirmedProperties,
  BookPickupAppointmentClickedProperties,
  CommunicationPreferences,
  CommunicationPreferencesSavedProperties,
  CustomDestination,
  CustomerAddressEditedProperties,
  CustomerHubOpenedProperties,
  CustomerInformationEditedProperties,
  CustomerSelectedProperties,
  FaqOpenProperties,
  ItemsInCartLoadedProperties,
  LastPlatformValueType,
  NbaCardViewedProperties,
  NbaFeedbackSubmittedProperties,
  NewArrivalsButtonClickedProperties,
  PlatformValueType,
  OrderClickedProperties,
  ReturnedToAclProperties,
  ReturnOrderClickedProperties,
  SettingsAddressBookClickedProperties,
  SettingsCommunicationClickledProperties,
  SettingsCustomerInfoClickedProperties,
  ShowReturnLabelClickedProperties,
  TabClickedProperties,
  TrackYourOrderClickedProperties,
  WardrobeItemClickedProperties,
} from '../avo/avo'
import avo from '../avo/avo'
import { SEGMENT_WRITE_KEY } from '../config/segment.config'

const APPLICATION_ID = 'customer_hub' as unknown as ApplicationIdValueType
const APP_VERSION = '1.0.1'

let analyticsInitialized = false

export interface CustomerHubAnalyticsConfig {
  oneTrustId?: string
  trackingGaCookie?: string
}

/** Suppresses default Avo `[avo] ...` console output from the generated client. */
const silentAvoLogger = {
  logDebug: () => true,
  logWarn: () => true,
  logError: () => true,
} as const

const initAvo: NonNullable<
  InitializeSegmentOptions<AvoEnv, ApplicationIdValueType>['initAvo']
> = (options, systemProperties, destinationOptions, segmentDestination) =>
  avo.initAvo(
    { ...options, webDebugger: false, avoLogger: silentAvoLogger },
    {
      applicationId: systemProperties.applicationId,
      version: systemProperties.version,
      affiliateMarker: '',
      awc: '',
      // @susu/analytics-client types these as plain strings; Avo narrows them
      // to its own unions, and no longer accepts clientId / sessionId.
      platform: systemProperties.platform as PlatformValueType,
      lastPlatform: systemProperties.lastPlatform as LastPlatformValueType,
    },
    destinationOptions,
    segmentDestination,
  )

export interface CustomerHubAvoOptions {
  env: Environment
  /** The host's Segment destination (e.g. one backed by its own `window.analytics`). */
  destination: CustomDestination
  platform?: string
  lastPlatform?: string
}

let customerHubAvoInitialized = false

/**
 * Initialises this package's own generated Avo client against a destination the
 * host provides.
 *
 * `initializeAnalytics` does this too, but only when `App` is mounted. A host
 * that embeds `CustomerHubCore` directly (active-customers-list) never mounts
 * `App`, so this package's Avo module (its own `Segment` destination and system
 * properties, separate from the host's) stayed uninitialised: events were logged
 * as sent, carried `application_id: undefined`, and were dropped. The host calls
 * this with its own Segment destination so the events travel through its
 * Segment instance. `application_id: customer_hub` tells them apart.
 */
export function initializeCustomerHubAvo({
  env,
  destination,
  platform = '',
  lastPlatform = '',
}: CustomerHubAvoOptions) {
  if (customerHubAvoInitialized) return
  customerHubAvoInitialized = true

  try {
    avo.initAvo(
      {
        env: env === 'prd' ? avo.AvoEnv.Prod : avo.AvoEnv.Dev,
        webDebugger: false,
        avoLogger: silentAvoLogger,
      },
      {
        applicationId: APPLICATION_ID,
        version: APP_VERSION,
        affiliateMarker: '',
        awc: '',
        platform: platform as PlatformValueType,
        lastPlatform: lastPlatform as LastPlatformValueType,
      },
      {},
      destination,
    )
  } catch {
    // Analytics failures must never crash the host.
  }
}

const getAnalyticsEnvironmentConfig = (
  env: Environment,
  analyticsConfig: CustomerHubAnalyticsConfig,
): AnalyticsEnvironmentConfig => ({
  segmentWriteKey: SEGMENT_WRITE_KEY[env],
  trackingGaCookie: analyticsConfig.trackingGaCookie,
  environmentType: env === 'prd' ? 'PRD' : undefined,
  appVersion: APP_VERSION,
})

function safeTrack(fn: () => void) {
  try {
    fn()
  } catch {
    // Analytics failures (e.g. Segment blocked by an ad blocker) must never
    // crash the app. Errors are intentionally swallowed here.
  }
}
export function initializeAnalytics(
  env: Environment,
  analyticsConfig: CustomerHubAnalyticsConfig = {},
) {
  if (analyticsInitialized) return
  analyticsInitialized = true

  if (analyticsConfig.oneTrustId) {
    insertCookieTrackingScript(analyticsConfig.oneTrustId)
  }

  initializeSegment<AvoEnv, ApplicationIdValueType>({
    env: getAnalyticsEnvironmentConfig(env, analyticsConfig),
    applicationId: APPLICATION_ID,
    version: APP_VERSION,
    initAvo,
    useWithOneTrust: true,
  })
}

/**
 * GA4 identity for Avo payloads, read live from the `_ga` / `_ga_<container>`
 * cookies with the same reader the Segment plugin puts on every event — not the
 * localStorage `client_id`/`session_id`/`session_number` copy, which nothing has
 * written since DA-1340 and which sent a stale session next to the host's live
 * one (DA-1576). `null` when GA has not written the cookie (in-store, consent
 * declined, ad blocker), as before.
 */
export function getGAIdentityProperties() {
  const { ga_client_id, ga_session_id, ga_session_number } =
    getGoogleAnalyticsData()

  return {
    gaClientId: ga_client_id || null,
    gaSessionId: ga_session_id || null,
    gaSessionNumber: ga_session_number || null,
  }
}

/**
 * Marketing click IDs that Avo now requires on every event. They arrive as URL
 * query params on the landing hit; empty string when absent (non-nullable).
 */
export function getClickIdProperties() {
  const params =
    typeof window === 'undefined'
      ? null
      : new URLSearchParams(window.location.search)
  const read = (key: string) => params?.get(key) ?? ''

  return {
    ttclid: read('ttclid'),
    msclkid: read('msclkid'),
    twclid: read('twclid'),
    rdtCid: read('rdt_cid'),
    epik: read('epik'),
    awc: read('awc'),
  }
}

export function trackFaqOpen(properties: FaqOpenProperties) {
  safeTrack(() => avo.faqOpen(properties))
}

export function trackCustomerInformationEdited(
  properties: CustomerInformationEditedProperties,
) {
  safeTrack(() => avo.customerInformationEdited(properties))
}

export function trackCustomerHubOpened(
  properties: CustomerHubOpenedProperties,
) {
  safeTrack(() => avo.customerHubOpened(properties))
}

export function trackCustomerSelected(properties: CustomerSelectedProperties) {
  safeTrack(() => avo.customerSelected(properties))
}

export function trackReturnOrderClicked(
  properties: ReturnOrderClickedProperties,
) {
  safeTrack(() => avo.returnOrderClicked(properties))
}

export function trackCancellationClicked(
  properties: CancellationClickedProperties,
) {
  safeTrack(() => avo.cancellationClicked(properties))
}

export function trackCancellationConfirmed(
  properties: CancellationConfirmedProperties,
) {
  safeTrack(() => avo.cancellationConfirmed(properties))
}

export function trackCustomerAddressEdited(
  properties: CustomerAddressEditedProperties,
) {
  safeTrack(() => avo.customerAddressEdited(properties))
}

export function trackTabClicked(properties: TabClickedProperties) {
  safeTrack(() => avo.tabClicked(properties))
}

export function trackReturnedToAcl(properties: ReturnedToAclProperties) {
  safeTrack(() => avo.returnedToAcl(properties))
}

export function trackOrderClicked(properties: OrderClickedProperties) {
  safeTrack(() => avo.orderClicked(properties))
}

export function trackCommunicationPreferencesSaved(
  properties: CommunicationPreferencesSavedProperties,
) {
  safeTrack(() => avo.communicationPreferencesSaved(properties))
}

export function trackNewArrivalsButtonClicked(
  properties: NewArrivalsButtonClickedProperties,
) {
  safeTrack(() => avo.newArrivalsButtonClicked(properties))
}

export function trackItemsInCartLoaded(
  properties: ItemsInCartLoadedProperties,
) {
  safeTrack(() => avo.itemsInCartLoaded(properties))
}

export function trackSettingsCustomerInfoClicked(
  properties: SettingsCustomerInfoClickedProperties,
) {
  safeTrack(() => avo.settingsCustomerInfoClicked(properties))
}

export function trackSettingsAddressBookClicked(
  properties: SettingsAddressBookClickedProperties,
) {
  safeTrack(() => avo.settingsAddressBookClicked(properties))
}

export function trackSettingsCommunicationClicked(
  properties: SettingsCommunicationClickledProperties,
) {
  safeTrack(() => avo.settingsCommunicationClickled(properties))
}

export function trackWardrobeItemClicked(
  properties: WardrobeItemClickedProperties,
) {
  safeTrack(() => avo.wardrobeItemClicked(properties))
}

export function trackActiveCardTrackOrderClicked(
  properties: TrackYourOrderClickedProperties,
) {
  safeTrack(() => avo.trackYourOrderClicked(properties))
}

export function trackActiveCardShowReturnLabelClicked(
  properties: ShowReturnLabelClickedProperties,
) {
  safeTrack(() => avo.showReturnLabelClicked(properties))
}

export function trackActiveCardBookPickupAppointmentClicked(
  properties: BookPickupAppointmentClickedProperties,
) {
  safeTrack(() => avo.bookPickupAppointmentClicked(properties))
}

export function trackNbaCardViewed(properties: NbaCardViewedProperties) {
  safeTrack(() => avo.nbaCardViewed(properties))
}

export function trackNbaFeedbackSubmitted(
  properties: NbaFeedbackSubmittedProperties,
) {
  safeTrack(() => avo.nbaFeedbackSubmitted(properties))
}

export type { CommunicationPreferences }
